import React, { useState, useEffect } from "react";
import { useQuery, useLazyQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import XLSX from "xlsx";
import IconButton from "@material-ui/core/IconButton";

import ReceiptIcon from "@material-ui/icons/Receipt";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import RefreshIcon from "@material-ui/icons/Refresh";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import FileCopyIcon from "@material-ui/icons/FileCopy";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import TransactionDetails from "./TransactionDetails";
import DialogShell from "designV2/components/DialogShell";
import PageHeader from "designV2/components/PageHeader";
import PageActionButton from "designV2/components/PageActionButton";
import MetricCard from "designV2/components/MetricCard";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import NumberRangeFilter from "designV2/components/NumberRangeFilter";
import DateRangeFilter from "designV2/components/DateRangeFilter";
import ActiveFilterChipList from "designV2/components/ActiveFilterChipList";
import StatusBadge from "designV2/components/StatusBadge";
import EmptyState from "designV2/components/EmptyState";
import PaginationComponent from "designV2/components/Pagination";
import { MetricsSkeleton, TableSkeleton } from "designV2/components/Skeleton";
import "../superFinance.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 * ------------------------------------------------------------------ */

const TRANSACTION_FILTERS = `
  _id: $_id
  transactionId: $transactionId
  status: $status
  type: $type
  amountFrom: $amountFrom
  amount: $amount
  createdAtFrom: $createdAtFrom
  createdAt: $createdAt
  paidAtFrom: $paidAtFrom
  paidAt: $paidAt
  driverName: $driverName
  driverPhoneNumber: $driverPhoneNumber
  passengerName: $passengerName
  passengerPhoneNumber: $passengerPhoneNumber
  shopName: $shopName
  shopPhoneNumber: $shopPhoneNumber
`;

const TRANSACTION_VARIABLES = `
  $limit: Int
  $skip: Int
  $_id: ID
  $transactionId: String
  $status: PaymentStatus
  $type: PaymentType
  $amountFrom: Float
  $amount: Float
  $createdAtFrom: Date
  $createdAt: Date
  $paidAtFrom: Date
  $paidAt: Date
  $createdAtSort: Int
  $paidAtSort: Int
  $amountSort: Int
  $driverName: String
  $driverPhoneNumber: String
  $passengerName: String
  $passengerPhoneNumber: String
  $shopName: String
  $shopPhoneNumber: String
`;

const GET_TRANSACTIONS = gql`
  query getTransactionsByAdmin(${TRANSACTION_VARIABLES}) {
    getTransactionsByAdmin(
      pagination: { limit: $limit, skip: $skip }
      filters: { ${TRANSACTION_FILTERS} }
      sort: { createdAt: $createdAtSort, paidAt: $paidAtSort, amount: $amountSort }
    ) {
      _id
      transactionId
      paymentIntent
      status
      type
      amount
      reversed
      reversalId
      refundId
      paidAt
      createdAt
      updatedAt
      user {
        _id
        fullName
        phoneNumber
      }
      driver {
        _id
        fullName
        phoneNumber
      }
      shop {
        _id
        name
        phoneNumbers
      }
      payments {
        _id
        for
        status
        type
        description
        amount
        createdAt
        updatedAt
        trip {
          _id
        }
        order {
          _id
        }
        shop {
          _id
          name
        }
        driver {
          _id
          fullName
        }
      }
    }
    getTransactionsByAdminCount(filters: { ${TRANSACTION_FILTERS} })
  }
`;

const GET_STATUS_COUNTS = gql`
  query getTransactionStatusCounts {
    paid: getTransactionsByAdminCount(filters: { status: PAID })
    unpaid: getTransactionsByAdminCount(filters: { status: UNPAID })
    failed: getTransactionsByAdminCount(filters: { status: FAILED })
    failedDueToRejection: getTransactionsByAdminCount(filters: { status: FAILED_DUE_TO_REJECTION })
  }
`;

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

const STATUS_OPTIONS = [
  { value: "PAID", label: "Paid" },
  { value: "UNPAID", label: "Unpaid" },
  { value: "FAILED", label: "Failed" },
  { value: "FAILED_DUE_TO_REJECTION", label: "Failed (rejection)" },
];
const STATUS_LABELS = STATUS_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});
const STATUS_TONES = {
  PAID: "success",
  UNPAID: "warning",
  FAILED: "danger",
  FAILED_DUE_TO_REJECTION: "danger",
};

const TYPE_OPTIONS = [
  { value: "PAY_TO_SPARK", label: "Pay to Spark" },
  { value: "PAY_TO_SHOP", label: "Pay to shop" },
  { value: "PAY_TO_DRIVER", label: "Pay to driver" },
  { value: "REJECTION_PENALTY", label: "Rejection penalty" },
];
const TYPE_LABELS = TYPE_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const SEARCH_TARGETS = [
  { value: "transactionId", label: "Transaction ref" },
  { value: "_id", label: "Transaction ID" },
  { value: "shop", label: "Shop" },
  { value: "driver", label: "Driver" },
  { value: "passenger", label: "Customer" },
];
const SEARCH_LABELS = SEARCH_TARGETS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});
const SEARCH_PLACEHOLDERS = {
  transactionId: "Search by transaction reference...",
  _id: "Search by transaction ID (24 characters)...",
  shop: "Search by shop name or phone number...",
  driver: "Search by driver name or phone number...",
  passenger: "Search by customer name or phone number...",
};

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "paidRecent", label: "Recently paid" },
  { value: "amountHigh", label: "Amount high-low" },
  { value: "amountLow", label: "Amount low-high" },
];

// Reversed or refunded money is the thing an operator is usually hunting
// for, and it was previously only visible by opening each row.
const FLOW_OPTIONS = [
  { value: "", label: "All transactions" },
  { value: "reversed", label: "Reversed only" },
  { value: "refunded", label: "Refunded only" },
  { value: "clean", label: "Neither" },
];
const FLOW_LABELS = FLOW_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const FILTER_DEBOUNCE_MS = 350;

const looksLikePhone = (value) => /^[\d\s+()-]+$/.test(value);
const isObjectId = (value) => /^[0-9a-fA-F]{24}$/.test(value);

const formatDate = (value) => (value ? moment(value).format("MMM D, YYYY") : "");
const formatTime = (value) => (value ? moment(value).format("hh:mm A") : "");

const partyOf = (transaction) => {
  if (transaction.shop) return { role: "Shop", name: transaction.shop.name };
  if (transaction.driver) return { role: "Driver", name: transaction.driver.fullName };
  if (transaction.user) return { role: "Customer", name: transaction.user.fullName };
  return null;
};

const Transactions = () => {
  const [searchTarget, setSearchTarget] = useState("transactionId");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [flowFilter, setFlowFilter] = useState("");
  const [amountFrom, setAmountFrom] = useState("");
  const [amountTo, setAmountTo] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [paidFrom, setPaidFrom] = useState("");
  const [paidTo, setPaidTo] = useState("");
  const [sortOption, setSortOption] = useState("newest");

  const [pageCount, setPageCount] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const [transactionDetail, setTransactionDetail] = useState(null);
  const [copiedId, setCopiedId] = useState("");
  const [exporting, setExporting] = useState(false);

  const [variables, setVariables] = useState({
    limit: perPage,
    skip: 0,
    createdAtSort: -1,
  });

  const trimmedTerm = searchTerm.trim();
  const searchIsUsable = !trimmedTerm || searchTarget !== "_id" || isObjectId(trimmedTerm);

  useEffect(() => {
    const handler = setTimeout(() => {
      const term = searchTerm.trim();
      const search = {};
      if (term) {
        if (searchTarget === "transactionId") search.transactionId = term;
        else if (searchTarget === "_id") {
          if (isObjectId(term)) search._id = term;
        } else if (looksLikePhone(term)) {
          search[`${searchTarget}PhoneNumber`] = term;
        } else {
          search[`${searchTarget}Name`] = term;
        }
      }

      setVariables((v) => ({
        ...v,
        skip: 0,
        _id: undefined,
        transactionId: undefined,
        driverName: undefined,
        driverPhoneNumber: undefined,
        passengerName: undefined,
        passengerPhoneNumber: undefined,
        shopName: undefined,
        shopPhoneNumber: undefined,
        ...search,
        status: statusFilter || undefined,
        type: typeFilter || undefined,
        amountFrom: amountFrom ? parseFloat(amountFrom) : undefined,
        amount: amountTo ? parseFloat(amountTo) : undefined,
        createdAtFrom: dateFrom || undefined,
        createdAt: dateTo || undefined,
        paidAtFrom: paidFrom || undefined,
        paidAt: paidTo || undefined,
        createdAtSort: sortOption === "oldest" ? 1 : sortOption === "newest" ? -1 : undefined,
        paidAtSort: sortOption === "paidRecent" ? -1 : undefined,
        amountSort: sortOption === "amountHigh" ? -1 : sortOption === "amountLow" ? 1 : undefined,
      }));
      setPageCount(0);
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [
    searchTarget,
    searchTerm,
    statusFilter,
    typeFilter,
    amountFrom,
    amountTo,
    dateFrom,
    dateTo,
    paidFrom,
    paidTo,
    sortOption,
  ]);

  useEffect(() => {
    setVariables((v) => ({ ...v, limit: perPage, skip: perPage * pageCount }));
  }, [perPage, pageCount]);

  const { data, loading, error, refetch } = useQuery(GET_TRANSACTIONS, {
    variables,
    fetchPolicy: "network-only",
  });

  const { data: statusCountsData } = useQuery(GET_STATUS_COUNTS, {
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });

  const [fetchAllForExport, { data: exportData }] = useLazyQuery(GET_TRANSACTIONS, {
    fetchPolicy: "network-only",
  });

  const allTransactions = (data && data.getTransactionsByAdmin) || [];
  const serverCount = (data && data.getTransactionsByAdminCount) || 0;

  // `reversed` / `refundId` are not filterable server-side, so this one
  // narrows the page in the browser. Flagged in the UI as such, since it
  // means the pagination count no longer describes what you can see.
  const transactions = allTransactions.filter((transaction) => {
    if (flowFilter === "reversed") return Boolean(transaction.reversed);
    if (flowFilter === "refunded") return Boolean(transaction.refundId);
    if (flowFilter === "clean") return !transaction.reversed && !transaction.refundId;
    return true;
  });

  const pageAmount = transactions.reduce((sum, t) => sum + (t.amount || 0), 0);
  const counts = statusCountsData || {};
  const failedCount = (counts.failed || 0) + (counts.failedDueToRejection || 0);

  /* ---------------- filter chips ---------------- */

  const activeFilterChips = [];
  if (trimmedTerm) {
    activeFilterChips.push({ id: "search", label: `${SEARCH_LABELS[searchTarget]}: ${trimmedTerm}` });
  }
  if (statusFilter) activeFilterChips.push({ id: "status", label: `Status: ${STATUS_LABELS[statusFilter]}` });
  if (typeFilter) activeFilterChips.push({ id: "type", label: `Type: ${TYPE_LABELS[typeFilter]}` });
  if (flowFilter) activeFilterChips.push({ id: "flow", label: FLOW_LABELS[flowFilter] });
  if (amountFrom || amountTo) {
    activeFilterChips.push({ id: "amount", label: `Amount: $${amountFrom || "0"} to $${amountTo || "-"}` });
  }
  if (dateFrom || dateTo) {
    activeFilterChips.push({ id: "date", label: `Created: ${dateFrom || "-"} to ${dateTo || "-"}` });
  }
  if (paidFrom || paidTo) {
    activeFilterChips.push({ id: "paid", label: `Paid: ${paidFrom || "-"} to ${paidTo || "-"}` });
  }

  const clearFilter = (id) => {
    if (id === "search") setSearchTerm("");
    if (id === "status") setStatusFilter("");
    if (id === "type") setTypeFilter("");
    if (id === "flow") setFlowFilter("");
    if (id === "amount") {
      setAmountFrom("");
      setAmountTo("");
    }
    if (id === "date") {
      setDateFrom("");
      setDateTo("");
    }
    if (id === "paid") {
      setPaidFrom("");
      setPaidTo("");
    }
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setTypeFilter("");
    setFlowFilter("");
    setAmountFrom("");
    setAmountTo("");
    setDateFrom("");
    setDateTo("");
    setPaidFrom("");
    setPaidTo("");
  };

  /* ---------------- export ---------------- */

  useEffect(() => {
    if (!exporting || !exportData) return;
    const rows = [
      [
        "Transaction ref",
        "Transaction ID",
        "Counterparty",
        "Role",
        "Type",
        "Status",
        "Amount",
        "Payments",
        "Reversed",
        "Refund ID",
        "Paid at",
        "Created",
      ],
    ];
    exportData.getTransactionsByAdmin.forEach((t) => {
      const party = partyOf(t);
      rows.push([
        t.transactionId || "-",
        t._id,
        (party && party.name) || "-",
        (party && party.role) || "-",
        TYPE_LABELS[t.type] || t.type || "-",
        STATUS_LABELS[t.status] || t.status,
        Number(t.amount || 0).toFixed(2),
        (t.payments && t.payments.length) || 0,
        t.reversed ? "yes" : "no",
        t.refundId || "-",
        t.paidAt ? moment(t.paidAt).format("MM/DD/YYYY HH:mm") : "-",
        moment(t.createdAt).format("MM/DD/YYYY HH:mm"),
      ]);
    });
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Transactions");
    XLSX.writeFile(wb, "Transactions.xlsx");
    setExporting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportData]);

  const handleExport = () => {
    if (serverCount === 0 || exporting) return;
    setExporting(true);
    fetchAllForExport({ variables: { ...variables, skip: 0, limit: serverCount } });
  };

  const handleCopy = (value) => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopiedId(value);
      window.setTimeout(() => setCopiedId(""), 1500);
    });
  };

  /* ---------------- render ---------------- */

  if (loading && !data) {
    return (
      <div className="page superFinancePage superTransactionsPage">
        <main className="main">
          <PageHeader
            title="Transactions"
            description="Money actually moved: each transaction and the payments it settled."
          />
          <MetricsSkeleton count={4} />
          <TableSkeleton rows={6} columns={7} />
        </main>
      </div>
    );
  }

  if (error) {
    return <ErrorPage />;
  }

  return (
    <div className="page superFinancePage superTransactionsPage">
      <main className="main">
        <PageHeader
          title="Transactions"
          count={serverCount}
          description="Money actually moved: each transaction, who it was with, and the payments it settled."
          actions={
            <PageActionButton
              icon={<CloudDownloadIcon fontSize="small" />}
              label={exporting ? "Exporting" : "Export"}
              info={
                exporting ? "Preparing file" : `${serverCount} transaction${serverCount === 1 ? "" : "s"}`
              }
              onClick={handleExport}
              disabled={serverCount === 0}
              loading={exporting}
            />
          }
        />

        <section className="stats">
          <MetricCard
            icon={<ReceiptIcon />}
            title="Matching transactions"
            value={serverCount}
            hint={activeFilterChips.length > 0 ? "With current filters" : "All transactions"}
          />
          <MetricCard icon={<CheckCircleOutlineIcon />} title="Paid" value={counts.paid || 0} tone="success" />
          <MetricCard icon={<HourglassEmptyIcon />} title="Unpaid" value={counts.unpaid || 0} tone="warning" />
          <MetricCard
            icon={<ErrorOutlineIcon />}
            title="Failed"
            value={failedCount}
            tone={failedCount > 0 ? "danger" : "default"}
          />
        </section>

        <section className="filters">
          <SearchField
            placeholder={SEARCH_PLACEHOLDERS[searchTarget]}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="filtersSelects">
            <FilterDropdown
              label={`Search in: ${SEARCH_LABELS[searchTarget]}`}
              options={SEARCH_TARGETS}
              value={searchTarget}
              active={false}
              onChange={(e) => setSearchTarget(e.target.value)}
            />
            <FilterDropdown
              label={statusFilter ? `Status: ${STATUS_LABELS[statusFilter]}` : "Status"}
              options={[{ value: "", label: "All statuses" }, ...STATUS_OPTIONS]}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            />
            <FilterDropdown
              label={typeFilter ? `Type: ${TYPE_LABELS[typeFilter]}` : "Type"}
              options={[{ value: "", label: "All types" }, ...TYPE_OPTIONS]}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            />
            <FilterDropdown
              label={flowFilter ? FLOW_LABELS[flowFilter] : "Reversal"}
              options={FLOW_OPTIONS}
              value={flowFilter}
              onChange={(e) => setFlowFilter(e.target.value)}
            />
            <NumberRangeFilter
              label="Amount"
              fromValue={amountFrom}
              toValue={amountTo}
              onFromChange={(e) => setAmountFrom(e.target.value)}
              onToChange={(e) => setAmountTo(e.target.value)}
            />
            <DateRangeFilter
              label="Created"
              fromValue={dateFrom}
              toValue={dateTo}
              onFromChange={(e) => setDateFrom(e.target.value)}
              onToChange={(e) => setDateTo(e.target.value)}
            />
            <DateRangeFilter
              label="Paid"
              fromValue={paidFrom}
              toValue={paidTo}
              onFromChange={(e) => setPaidFrom(e.target.value)}
              onToChange={(e) => setPaidTo(e.target.value)}
              align="right"
            />
            <FilterDropdown
              label={`Sort: ${SORT_OPTIONS.find((o) => o.value === sortOption).label}`}
              options={SORT_OPTIONS}
              value={sortOption}
              active={false}
              align="right"
              onChange={(e) => setSortOption(e.target.value)}
            />
          </div>
        </section>

        {!searchIsUsable && (
          <div className="dsFormMessage warning" role="status">
            A transaction ID is 24 characters — still waiting for a complete one before searching.
          </div>
        )}

        {activeFilterChips.length > 0 && (
          <ActiveFilterChipList
            filters={activeFilterChips}
            onClearFilter={clearFilter}
            onClearAll={clearAllFilters}
          />
        )}

        <div className="tableTopBar">
          <div className="tableTopBarLeft">
            {transactions.length > 0 && (
              <span className="superFinancePill">This page: ${pageAmount.toFixed(2)}</span>
            )}
            {flowFilter && (
              <span className="superFinancePill">
                {transactions.length} of {allTransactions.length} on this page
              </span>
            )}
          </div>
          <div className="tableTopBarRight">
            <IconButton
              className="tableTopIconButton"
              size="small"
              onClick={() => refetch()}
              title="Refresh transactions"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        <div className={`tableResponsive ${loading && data ? "isRefetching" : ""}`}>
          {loading && data && <span className="superFinanceRefetchSpinner" aria-hidden="true" />}
          <section className="table">
            <div className="tableHeader">
              <div className="tableHeaderCell">Transaction</div>
              <div className="tableHeaderCell">Counterparty</div>
              <div className="tableHeaderCell">Type</div>
              <div className="tableHeaderCell">Status</div>
              <div className="tableHeaderCell">Amount</div>
              <div className="tableHeaderCell">Paid</div>
              <div className="tableHeaderCell tableHeaderAction">Actions</div>
            </div>

            {transactions.length === 0 ? (
              <EmptyState
                icon={<ReceiptIcon />}
                title={activeFilterChips.length === 0 ? "No transactions yet" : "No matches"}
                message={
                  activeFilterChips.length === 0
                    ? "Transactions appear here once payments start settling."
                    : "No transactions match these filters. Try widening or clearing them."
                }
              />
            ) : (
              transactions.map((transaction) => {
                const party = partyOf(transaction);
                const paymentCount = (transaction.payments && transaction.payments.length) || 0;
                return (
                  <div className="row" key={transaction._id}>
                    <div className="superFinanceRefCell" data-label="Transaction">
                      {transaction.transactionId ? (
                        <React.Fragment>
                          <span className="superFinanceRef">{transaction.transactionId}</span>
                          <IconButton
                            size="small"
                            className="rowActionButton"
                            onClick={() => handleCopy(transaction.transactionId)}
                            title="Copy reference"
                          >
                            {copiedId === transaction.transactionId ? (
                              <CheckCircleOutlineIcon fontSize="small" />
                            ) : (
                              <FileCopyIcon style={{ fontSize: 16 }} />
                            )}
                          </IconButton>
                        </React.Fragment>
                      ) : (
                        <span className="superFinanceRefMuted">No reference</span>
                      )}
                    </div>

                    <div className="superFinanceParty" data-label="Counterparty">
                      {party ? (
                        <React.Fragment>
                          <span className="superFinancePartyName">{party.name || "Unnamed"}</span>
                          <span className="superFinancePartyRole">{party.role}</span>
                        </React.Fragment>
                      ) : (
                        <span className="superFinanceRefMuted">-</span>
                      )}
                    </div>

                    <div className="superFinanceTypeCell" data-label="Type">
                      <span>{TYPE_LABELS[transaction.type] || transaction.type || "-"}</span>
                      <span className="superFinanceSubtext">
                        {paymentCount} payment{paymentCount === 1 ? "" : "s"}
                      </span>
                    </div>

                    <span data-label="Status">
                      <span className="superFinanceBadges">
                        <StatusBadge
                          status={STATUS_TONES[transaction.status] || "neutral"}
                          label={STATUS_LABELS[transaction.status] || transaction.status}
                        />
                        {transaction.reversed && <StatusBadge status="danger" label="Reversed" dot={false} />}
                        {transaction.refundId && !transaction.reversed && (
                          <StatusBadge status="warning" label="Refunded" dot={false} />
                        )}
                      </span>
                    </span>

                    <span
                      className={`superFinanceAmount ${transaction.reversed ? "isReversed" : ""}`}
                      data-label="Amount"
                    >
                      ${Math.abs(transaction.amount || 0).toFixed(2)}
                    </span>

                    <span className="updated" data-label="Paid">
                      {transaction.paidAt ? (
                        <React.Fragment>
                          <strong>{formatDate(transaction.paidAt)}</strong>
                          <span>{formatTime(transaction.paidAt)}</span>
                        </React.Fragment>
                      ) : (
                        <span className="superFinanceRefMuted">Not paid</span>
                      )}
                    </span>

                    <div className="actions">
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() => setTransactionDetail(transaction)}
                        title="View transaction"
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                );
              })
            )}
          </section>
        </div>

        {serverCount > 0 && (
          <PaginationComponent
            currentPage={pageCount}
            pageSize={perPage}
            totalCount={serverCount}
            pageSizeOptions={[10, 20, 50]}
            onPageChange={(newPage) => setPageCount(newPage)}
            onPageSizeChange={(size) => {
              setPerPage(parseInt(size, 10));
              setPageCount(0);
            }}
          />
        )}
      </main>

      <DialogShell
        open={Boolean(transactionDetail)}
        onClose={() => setTransactionDetail(null)}
        title="Transaction details"
      >
        {transactionDetail && (
          <TransactionDetails
            transaction={transactionDetail}
            close={() => setTransactionDetail(null)}
            refetch={() => refetch()}
            hasRefunder={true}
          />
        )}
      </DialogShell>
    </div>
  );
};

export default Transactions;
